import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Grid,
  LinearProgress,
  Paper,
  Snackbar,
  SnackbarCloseReason,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import {
  VTextField,
  VForm,
  useVForm,
  IVFormErrors,
  VSwitch,
  AutoCompleteProdutividade,
  VFieldMoney,
} from "../../../shared/forms";
import { FerramentasDeDetalhe } from "../../../shared/components";
import { LayoutBaseDePagina } from "../../../shared/layouts";
import { ProducaoService } from "../../../shared/services/api/Producao/ProducaoService";

interface IFormData {
  idproducao: string;
  vlproducao: number;
  meta: number;
  vladicional: number;
  status: boolean;
}

const formValidationSchema: yup.ObjectSchema<IFormData> = yup.object().shape({
  idproducao: yup.string().required(),
  vlproducao: yup.number().required(),
  meta: yup.number().required(),
  vladicional: yup.number().required(),
  status: yup.boolean().optional().default(true),
});

export const ConfProdutividade: React.FC = () => {
  const { id = "novo" } = useParams<"id">();
  const navigate = useNavigate();
  const { formRef, save, saveAndClose } = useVForm();
  const [isLoading, setIsLoading] = useState(false);
  const [descricao, setDescricao] = useState("");
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState("");
  const [tms, setTms] = useState<"warning" | "error" | "info" | "success">("warning");
  const [openConfirm, setOpenConfirm] = useState(false);

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === "clickaway") return;
    setOpenmsg(false);
    if (tms === "success") navigate("/confprocucao");
  };

  const handleDelete = () => setOpenConfirm(true);

  const handleConfirmDelete = () => {
    ProducaoService.deleteById(id).then((result) => {
      if (result instanceof Error) {
        setOpenConfirm(false);
        setTms("error");
        setMsg(result.message);
        setOpenmsg(true);
      } else {
        setOpenConfirm(false);
        setTms("success");
        setMsg("Registro apagado com sucesso!");
        setOpenmsg(true);
      }
    });
  };

  const handleCancelDelete = () => setOpenConfirm(false);

  useEffect(() => {
    if (id !== "novo") {
      setIsLoading(true);
      ProducaoService.getById(id).then((result) => {
        setIsLoading(false);
        if (result instanceof Error) {
          setTms("error");
          setMsg(result.message);
          setOpenmsg(true);
        } else {
          formRef.current?.setData(result);
        }
      });
    } else {
      formRef.current?.setData({
        vlproducao: 0,
        meta: 0,
        vladicional: 0,
        status: true,
      });
    }
  }, [id]);

  const handleSave = (dados: IFormData) => {
    formValidationSchema
      .validate(dados, { abortEarly: false })
      .then((dadosValidados) => {
        setIsLoading(true);
        if (id === "novo") {
          ProducaoService.create(dadosValidados).then((result) => {
            setIsLoading(false);
            if (result instanceof Error) {
              setTms("error");
              setMsg(result.message);
              setOpenmsg(true);
            } else {
              setTms("success");
              setMsg("Lançamento Realizado com sucesso!");
              setOpenmsg(true);
            }
          });
        } else {
          ProducaoService.updateById(id, dadosValidados).then((result) => {
            setIsLoading(false);
            if (result instanceof Error) {
              setTms("error");
              setMsg(result.message);
              setOpenmsg(true);
            } else {
              setTms("success");
              setMsg("Alteração concluída com sucesso!");
              setOpenmsg(true);
            }
          });
        }
      })
      .catch((errors: yup.ValidationError) => {
        const validationErrors: IVFormErrors = {};
        errors.inner.forEach((error) => {
          if (!error.path) return;
          validationErrors[error.path] = error.message;
        });
        formRef.current?.setErrors(validationErrors);
      });
  };

  return (
    <LayoutBaseDePagina
      titulo={id === "novo" ? "Nova Configuração" : descricao}
      barraDeFerramentas={
        <FerramentasDeDetalhe
          textoBotaoNovo="Nova"
          mostrarBotaoSalvarEFechar
          mostrarBotaoSalvarCarregando={isLoading}
          mostrarBotaoSalvarEFecharCarregando={isLoading}
          mostrarBotaoImpressao={false}
          mostrarBotaoNovo={id !== "novo"}
          mostrarBotaoApagar={id !== "novo"}
          mostrarBotaoApagarCarregando={isLoading}
          aoClicarEmSalvar={save}
          aoClicarEmSalvarEFechar={saveAndClose}
          aoClicarEmVoltar={() => navigate("/confprocucao")}
          mostrarBotaoVoltarCarregando={isLoading}
          aoClicarEmApagar={() => handleDelete()}
          aoClicarEmNovo={() => navigate("/confprodutividade/detalhe/novo")}
          mostrarBotaoNovoCarregando={isLoading}
        />
      }
    >
      <VForm ref={formRef} onSubmit={handleSave}>
        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          open={openmsg}
          autoHideDuration={900}
          onClose={handleCloseMsg}
        >
          <Alert onClose={handleCloseMsg} severity={tms} variant="filled" sx={{ width: "100%" }}>
            {msg}
          </Alert>
        </Snackbar>
        <Snackbar open={openConfirm} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
          <Alert
            severity="warning"
            variant="filled"
            sx={{ display: "flex", alignItems: "center", width: "100%" }}
            action={
              <>
                <Button color="inherit" size="small" onClick={handleConfirmDelete}>Sim</Button>
                <Button color="inherit" size="small" onClick={handleCancelDelete}>Não</Button>
              </>
            }
          >
            Tem certeza que deseja excluir este registro?
          </Alert>
        </Snackbar>
        <Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined">
          <Grid container direction="column" padding={1} spacing={1}>
            {isLoading && (
              <Grid item>
                <LinearProgress variant="indeterminate" />
              </Grid>
            )}
            <Grid item>
              <Typography variant="h6">Geral</Typography>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
              <Grid container item direction="row" spacing={1} padding={1}>
                <Grid item xs={12} sm={12} md={8} lg={4} xl={4}>
                  <AutoCompleteProdutividade
                    isExternalLoading={id === "novo" ? isLoading : true}
                  />
                </Grid>
                <Grid item xs={6} sm={6} md={3} lg={2} xl={2}>
                  <VFieldMoney fullWidth size="small" name="vlproducao" label="Valor Produção" />
                </Grid>
                <Grid item xs={6} sm={6} md={3} lg={2} xl={2}>
                  <VFieldMoney fullWidth size="small" name="meta" label="Meta" />
                </Grid>
                <Grid item xs={6} sm={6} md={3} lg={2} xl={2}>
                  <VFieldMoney fullWidth size="small" name="vladicional" label="Valor Adicional" />
                </Grid>
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={6} xl={6} padding={1}>
                <Grid container item direction="row" spacing={2}>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={1}>
                    {id != "novo" && (
                      <VSwitch size="small" name="status" label="Ativo" fdisabled={isLoading} />
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </VForm>
    </LayoutBaseDePagina>
  );
};